<template>
  <div class="body-checklist">
    <ul class="int-checklist" ref="listContainer">
      <li
        v-for="(checklistItem, index) in checklistItems"
        :key="index"
        class="checklist-item"
        :class="{
          '-complete': indexStatus[index] === 'complete',
          '-skip': indexStatus[index] === 'skip',
          '-current': index === currentIndex,
        }"
        role="button"
        tabindex="0"
        aria-label="Complete checklist item"
        @click.prevent="toggleComplete(index)"
        @keyup.enter="toggleComplete(index)"
      >
        <span class="list-item-ico" aria-hidden="true">
          <i class="material-symbols-outlined fill" v-if="indexStatus[index] === 'complete'"
            >check_circle</i
          >
          <i
            class="material-symbols-outlined fill -mirror"
            v-else-if="indexStatus[index] === 'skip'"
            >replay_circle_filled</i
          >
          <i class="material-symbols-outlined" v-else>radio_button_unchecked</i>
        </span>
        {{ checklistItem }}
      </li>
    </ul>
  </div>
</template>
<script>
import { defineComponent } from 'vue'
import { scrollToElemTop } from '@/utils/scroll-to'

/**
 * Simple checklist that scrolls to the next "unchecked" item. No progress
 * is saved to user profile or between page refreshes.
 */
export default defineComponent({
  name: 'ScrollChecklist',
  components: {},
  props: {
    checklistItems: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      // map of index: complete, skip, incomplete to quickly see if an item is complete
      indexStatus: {},
    }
  },
  computed: {
    /**
     * Calculate the current / most recent incomplete item.  Should be the first
     * non-complete (incomplete / skip) item after the last completed one.
     */
    currentIndex() {
      // filter to just completed, convert to number, and sort
      const completed = Object.keys(this.indexStatus)
        .filter((key) => this.indexStatus[key] === 'complete')
        .map((key) => parseInt(key))
        .sort((a, b) => parseInt(a) - parseInt(b))

      // default to first checklist item
      let currentIndex = 0

      // if there are completed items, set to next index... but if on the
      // last checklist item, just set to null (no current index)
      if (completed.length) {
        const lastCompletedIndex = completed[completed.length - 1]
        const lastIndex = this.checklistItems.length - 1

        currentIndex = lastCompletedIndex < lastIndex ? lastCompletedIndex + 1 : null
      }

      return currentIndex
    },
  },
  methods: {
    /**
     * Fully rebuild and replace indexStatus state property.
     * - toggle complete / incomplete for index passed in
     * - set any previous to skipped if user jumps around int he list
     * - if incompleting, then set incomplete all in front of index
     */
    toggleComplete(index) {
      // track new status for index outside of loop
      const newIndexStatus = this.indexStatus[index] !== 'complete' ? 'complete' : 'incomplete'

      // build new indexStatus from all passed in checklistItems
      const newStatus = Object.keys(this.checklistItems).reduce((acc, key) => {
        // keep the existing value
        if (this.indexStatus[key]) {
          acc[key] = this.indexStatus[key]
        }

        // toggle complete / incomplete
        if (Number.parseInt(key) === index) {
          acc[key] = newIndexStatus
        }

        // handle previous / forward side effects

        // 1. if toggling to complete, mark any check items as "skipped" if the
        // user did not go in sequential order
        //
        // 2. if toggling to incomplete, incomplete any items in front of index
        // being toggled.
        if (newIndexStatus === 'complete') {
          if (this.indexStatus[key] !== 'complete' && Number.parseInt(key) < index) {
            acc[key] = 'skip'
          }
        } else {
          if (Number.parseInt(key) > index) {
            acc[key] = 'incomplete'
          }
        }

        return acc
      }, {})

      this.indexStatus = newStatus

      // scroll to current index after state update
      this.$nextTick(() => this.scrollToCurrent())
    },

    /**
     * Autoscroll to this.currentIndex DOM element
     */
    scrollToCurrent() {
      // at end of list, no action
      if (this.currentIndex === null) {
        return
      }

      const currentItemElem = this.$refs.listContainer.querySelector(
        `:nth-child(${this.currentIndex + 1})`,
      )

      if (currentItemElem) {
        scrollToElemTop({
          elem: currentItemElem,
          includeHeaderOffsetY: true,
        })
      }
    },
  },
})
</script>
